import ReactJsonView, { ReactJsonViewProps } from "@microlink/react-json-view";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, BoxProps, Input, SxProps, Theme, Typography } from "@mui/material";
import { Signal, useSignal } from "@preact/signals-react";
import { Maybe } from "graphql/jsutils/Maybe";

import { FlexProperties, JsonView, StyleProperties } from "../../../resolvers-types";
import { GetTheme, setupHandlers, setupLocalState } from "../../library/dataService";
import { INGJsonViewProps, RuntimeContext } from "../../library/NGFieldExtensions";
import { getClassName, getsxObject } from "../../library/utils";

interface LocalState {
  config: JsonView;

  Classes: Signal<string[]>;
  Style: Signal<Maybe<StyleProperties> | Maybe<FlexProperties>>;
  Visible: Signal<boolean>;

  Data: Signal<string>;
  DataName: Signal<string>;
  Expanded: Signal<boolean>;
  Placeholder: Signal<string>;
  Search: Signal<string>;
  Title: Signal<string>;

  context: RuntimeContext;
}

interface ToggleProps extends Omit<BoxProps, "onClick"> {
  expanded: boolean;
  onToggle?: () => void;
}

export function Toggle({ expanded, title, sx, onToggle, ...restProps }: ToggleProps) {
  return (
    <Box sx={{ cursor: "pointer", display: "flex", gap: "4px", ...sx }} {...restProps} onClick={onToggle}>
      <KeyboardArrowRightIcon
        sx={{
          transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 50ms ease-in-out",
        }}
      />
      {title && (
        <Typography className="text-heading-small" sx={{ minWidth: "72px", userSelect: "none" }}>
          {title}
        </Typography>
      )}
    </Box>
  );
}

interface InspectorJsonViewProps extends ReactJsonViewProps {
  className?: string;
  sx?: SxProps<Theme>;
  containerProps?: BoxProps;
  placeholder: string;
  search: string;
  expanded: boolean;
  title: string;
  onChangeSearch: (value: string) => void;
  onToggleExpand: () => void;
}

export function InspectorJsonView({
  className,
  containerProps: { sx: containerSx = {}, ...restContainerProps } = {},
  placeholder,
  search,
  sx = {},
  title,
  expanded,
  onChangeSearch,
  onToggleExpand,

  theme,
  style,
  ...restReactJsonViewProps
}: InspectorJsonViewProps) {
  const combinedSx = { ...containerSx, ...sx };

  const darkColor = "#1e1e1e";
  const lightColor = "#fff";
  const backgroundColor = theme === "monokai" ? darkColor : lightColor;
  const backgroundColor2 = theme === "monokai" ? "darkColor" : "#e5f2f9";
  const color = theme === "monokai" ? lightColor : darkColor;
  const color2 = theme === "monokai" ? "#ababab" : "#d9d9d9";

  return (
    <Box
      className={`${className} inspector-panel inspector-${title.toLowerCase()}`}
      {...restContainerProps}
      sx={{
        ...combinedSx,
        flex: expanded ? "1 1 50%" : "0 0 auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          alignItems: "center",
          padding: "4px 8px 5px",
          position: "relative",
          backgroundColor: backgroundColor2,
          borderBottom: "1px solid",
          borderBottomColor: expanded ? color2 : "transparent",
        }}
      >
        <Toggle expanded={expanded} onToggle={onToggleExpand} title={title} />

        <Input
          disableUnderline
          sx={{
            borderRadius: "6px",
            backgroundColor: "white",
            color: "black",
            padding: "0 8px",
            flex: "1",
            border: "1px solid",
            borderColor: theme === "monokai" ? "#475467" : "#ccc",
            fontSize: "14px",
          }}
          placeholder={placeholder}
          value={search}
          onChange={(e) => onChangeSearch(e.currentTarget.value)}
          type="text"
        />
      </Box>

      <Box
        sx={{
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ddddde",
            "&:hover": {
              backgroundColor: "#c0c0c0",
            },
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: "red",
          },
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: expanded ? "flex" : "none",
        }}
      >
        <ReactJsonView
          style={{ flex: 1, overflow: "auto", backgroundColor, ...style }}
          theme={theme}
          {...restReactJsonViewProps}
        />
      </Box>
    </Box>
  );
}

export function NGJsonView({ config, context }: INGJsonViewProps) {
  // local state
  const local: LocalState = setupLocalState(
    config,
    {
      Classes: useSignal(config.Classes),
      Style: useSignal(config.Style ?? {}),
      Visible: useSignal(config.Visible ?? true),

      Data: useSignal(config.Data ?? '{ "key": "value" }'),
      DataName: useSignal(config.DataName ?? "root"),
      Expanded: useSignal(config.Expanded ?? true),
      Placeholder: useSignal(config.Placeholder ?? "Search..."),
      Search: useSignal(config.Search ?? ""),
      Title: useSignal(config.Title ?? "JSON View"),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  // variables
  const className = getClassName(local.Classes) ?? "";
  const style = getsxObject(local.Style.value);
  const { ThemeMode: theme } = GetTheme();
  const jsonViewTheme = theme === "dark" ? "monokai" : "rjv-default";

  // helpers
  const getDataAsJson = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn("Failed to parse JSON", e);
      return {};
    }
  };

  return local.Visible.value ? (
    <InspectorJsonView
      className={className}
      src={getDataAsJson(local.Data.value)}
      name={local.DataName.value}
      placeholder={local.Placeholder.value}
      search={local.Search.value}
      sx={style}
      title={local.Title.value}
      expanded={local.Expanded.value}
      shouldCollapse={undefined}
      onChangeSearch={(value) => {
        local.Search.value = value;
        handlers["onChangeSearch"]?.(value);
      }}
      onToggleExpand={() => {
        local.Expanded.value = !local.Expanded.value;
        handlers["onToggleExpand"]?.(local.Expanded.value);
      }}
      theme={jsonViewTheme}
    />
  ) : null;
}
